import React from "react";
import { commitMutation, graphql, QueryRenderer } from "react-relay";

import { faCopy, faExclamationTriangle, faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import copy from "copy-to-clipboard";
import { compact } from "lodash";
import moment from "moment";
import { Card, Input, Message, Segment, Table } from "semantic-ui-react";

import { prompt, warning } from "~/lib/prompts";
import type { NodesFromEdges } from "~/lib/utils";
import { environment } from "~/models";
import Button from "~/ui/button";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import toast from "~/ui/toast";

import { SettingsPageTitle } from "./SettingsModal/shared";

import type { APIKeysCreateMutation } from "~/__generated__/APIKeysCreateMutation.graphql";
import type { APIKeysQuery as QueryType } from "~/__generated__/APIKeysQuery.graphql";
import type { APIKeysRevokeMutation } from "~/__generated__/APIKeysRevokeMutation.graphql";

const CreateAPIKeyMutation = graphql`
	mutation APIKeysCreateMutation($name: String!) {
		apiKeys {
			create(name: $name) {
				token
				key {
					id
				}
			}
		}
	}
`;

const RevokeAPIKeyMutation = graphql`
	mutation APIKeysRevokeMutation($id: ID!) {
		apiKeys {
			revoke(id: $id) {
				ok
			}
		}
	}
`;

const APIKeysQuery = graphql`
	query APIKeysQuery {
		apiKeys {
			edges {
				node {
					id
					name
					createdAt
				}
			}
		}
	}
`;

type APIKey = NodesFromEdges<QueryType["response"]["apiKeys"]>;

interface APIKeysState {
	refetch: boolean;
	newToken: string | null | undefined;
}

export default class APIKeys extends React.Component<{}, APIKeysState> {
	state: APIKeysState = {
		refetch: false,
		newToken: null,
	};

	newTokenRef: React.RefObject<Input> = React.createRef();

	onGenerateToken = async () => {
		const name = await prompt("Enter a name for your new token:", {
			title: "Generate Authentication Token",
			yesText: "Generate",
		});

		if (!name || name === true) {
			return;
		}

		commitMutation<APIKeysCreateMutation>(environment, {
			mutation: CreateAPIKeyMutation,
			variables: {
				name,
			},

			onCompleted: props => {
				this.setState({
					refetch: !this.state.refetch,
					newToken: props.apiKeys?.create?.token,
				});
			},
		});
	};

	async onRevokeToken(key: APIKey) {
		const confirm = await warning(
			"Any applications or scripts that are using this token will stop working. This cannot be undone!",
			{
				yesText: "Delete Token",
				yesProps: { variant: "destructive" },
				title: "Delete token?",
			},
		);

		if (!confirm) {
			return;
		}

		commitMutation<APIKeysRevokeMutation>(environment, {
			mutation: RevokeAPIKeyMutation,
			variables: {
				id: key.id,
			},

			onCompleted: () => {
				this.setState({ refetch: !this.state.refetch });
			},
		});
	}

	onDismissNewToken = () => {
		this.setState({ newToken: null });
	};

	renderContents = ({ props: queryResponse }: { props: QueryType["response"] | null }) => {
		return (
			<Segment basic>
				<SettingsPageTitle>Authentication tokens</SettingsPageTitle>
				<p>These tokens will allow you to access your data in Fellow via the GraphQL API.</p>
				<p>
					To use a token, use HTTP Basic Authentication with your email address as the username and the token as the
					password.
				</p>
				{this.state.newToken ? (
					<Message info onDismiss={this.onDismissNewToken}>
						<Message.Header>Success!</Message.Header>
						<p>Here is your brand new authentication token:</p>
						<Input
							fluid
							readOnly
							ref={this.newTokenRef}
							// inputRef is not available in the semantic types.
							// eslint-disable-next-line fellow/no-inline-disable
							// eslint-disable-next-line react/jsx-no-bind, @typescript-eslint/no-explicit-any
							onFocus={() => (this.newTokenRef.current as any).inputRef.select()}
							action={{
								color: "teal",
								icon: <FontAwesomeIcon className="icon" icon={faCopy} />,
								onClick: () => {
									this.newTokenRef.current?.focus();
									if (this.state.newToken) copy(this.state.newToken);
									return toast.success("Copied to clipboard");
								},
								"data-tooltip": "Copy to Clipboard",
								"data-inverted": true,
								"data-position": "bottom center",
							}}
							className="mono"
							size="mini"
							value={this.state.newToken}
						/>
						<p>
							<strong>
								<FontAwesomeIcon icon={faExclamationTriangle} /> Be sure to write this down somewhere safe!
							</strong>{" "}
							This is the last time you will be able to see it.
						</p>
					</Message>
				) : undefined}
				<Button variant="primary" onClick={this.onGenerateToken}>
					Generate new token
				</Button>
				<Card fluid>
					<Card.Content className="header">Tokens</Card.Content>
					<Table basic padded className="content">
						<Table.Body>
							{!queryResponse?.apiKeys?.edges?.length ? (
								<Table.Row>
									<Table.Cell verticalAlign="middle" textAlign="center" colSpan={2}>
										{queryResponse ? (
											<Segment basic>
												<div className="meta">No authentication tokens.</div>
											</Segment>
										) : undefined}
									</Table.Cell>
								</Table.Row>
							) : undefined}
							{compact(compact(queryResponse?.apiKeys?.edges ?? []).map(edge => edge?.node)).map(key => (
								<Table.Row key={key.id}>
									<Table.Cell verticalAlign="middle">
										<span>{key.name}</span>
										<br />
										<small className="meta">
											Created{" "}
											<time
												data-tooltip={moment(key.createdAt || "").format("llll")}
												data-inverted
												data-position="bottom center"
											>
												{moment(key.createdAt || "").fromNow()}
											</time>
										</small>
									</Table.Cell>
									<Table.Cell collapsing verticalAlign="middle" textAlign="right">
										<Button
											variant="destructive"
											size="slim"
											data-tooltip="Delete Token"
											data-inverted
											data-position="bottom center"
											// eslint-disable-next-line fellow/no-inline-disable
											// eslint-disable-next-line react/jsx-no-bind
											onClick={this.onRevokeToken.bind(this, key)}
										>
											<FontAwesomeIcon className="icon" icon={faTrashCan} />
										</Button>
									</Table.Cell>
								</Table.Row>
							))}
						</Table.Body>
					</Table>
					{!queryResponse && <LoadingSpinner dimmer />}
				</Card>
			</Segment>
		);
	};

	render() {
		return (
			<QueryRenderer<QueryType>
				query={APIKeysQuery}
				environment={environment}
				variables={{ refetch: this.state.refetch }}
				// eslint-disable-next-line fellow/no-inline-disable
				// eslint-disable-next-line react/jsx-no-bind
				render={({ props }) => this.renderContents({ props })}
			/>
		);
	}
}
